import axios from 'axios';

const DEFAULT_API_BASE_URL = import.meta.env.PROD
  ? 'https://server.dizipay.in/api/v1'
  : 'http://localhost:5005/api/v1';

const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const configuredLooksLocal = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/api\/v1$/i.test(configuredApiBaseUrl || '');

export const API_BASE_URL = import.meta.env.PROD
  ? (configuredApiBaseUrl || DEFAULT_API_BASE_URL)
  : (configuredLooksLocal ? configuredApiBaseUrl : DEFAULT_API_BASE_URL);

let accessToken = null;
let refreshPromise = null;
let authFailureHandler = null;

export function setAccessToken(token) {
  accessToken = token || null;
}

export function getAccessToken() {
  return accessToken;
}

export function onAuthFailure(handler) {
  authFailureHandler = handler;
}

export class ApiError extends Error {
  constructor({ message, code, details, requestId, status }) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.requestId = requestId;
    this.status = status;
  }
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

apiClient.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    const hasRefreshSession = localStorage.getItem('servicedesk.hasRefreshSession') === 'true';
    if (status === 401 && hasRefreshSession && original && !original._retry && !original.url?.includes('/auth/refresh')) {
      original._retry = true;
      try {
        refreshPromise ||= apiClient.post('/auth/refresh');
        const refreshResponse = await refreshPromise;
        refreshPromise = null;
        const token = refreshResponse?.data?.accessToken;
        if (token) {
          setAccessToken(token);
          original.headers.Authorization = `Bearer ${token}`;
          return apiClient(original);
        }
      } catch (refreshError) {
        refreshPromise = null;
        setAccessToken(null);
        authFailureHandler?.(refreshError);
        throw refreshError;
      }
    }

    const payload = error.response?.data;
    throw new ApiError({
      message: payload?.error?.message || error.message || 'Request failed',
      code: payload?.error?.code || 'REQUEST_FAILED',
      details: payload?.error?.details || {},
      requestId: payload?.requestId || error.response?.headers?.['x-request-id'],
      status
    });
  }
);

export function unwrap(response) {
  return response?.data ?? response;
}
